import React, { useState, useEffect } from "react";
import GuestChat from "./GuestChat";

export default function ProjectModal({ project, isOpen, onClose, onRefresh }) {
  const [iframeKey, setIframeKey] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("ModalTheme");
    return saved || "light";
  });

  const iframeRules = project?.["iframe-rules"] || { orientation: "default" };

  // Reset iframe key and loading state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIframeKey(0);
      setIsIframeLoading(true);
    }
  }, [isOpen]);

  if (!isOpen || !project) return null;

  const siteUrl = project["is-bot"]
    ? project.livesite
    : project["iframe-site"] || project.livesite;
  const isBot = Boolean(project["is-bot"]);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("ModalTheme", newTheme);
    window.dispatchEvent(new Event("modalThemeChange"));
  };

  const handleRefresh = () => {
    setIsSpinning(true);
    setIsIframeLoading(true);
    setIframeKey((prev) => prev + 1);
    if (onRefresh) onRefresh();

    // Reset spinning state after animation completes
    setTimeout(() => {
      setIsSpinning(false);
    }, 500);
  };

  return (
    <div className="project-modal-overlay" onClick={onClose}>
      <div
        className={`project-modal orientation-${iframeRules.orientation || "default"}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme === "light" ? "#ffffff" : "#000c",
          border: theme === "light" ? "1px solid #0002" : "1px solid #fff2",
        }}
      >
        <div className={`project-modal-header theme-${theme}`}>
          <div
            className="project-modal-title"
            style={{ color: theme === "light" ? "#000" : "#fff" }}
          >
            {project.name}
          </div>
          <div className="project-modal-actions">
            {siteUrl && (
              <button
                type="button"
                className={`project-modal-btn external-btn${isBot ? " bot-btn" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(siteUrl, "_blank", "noopener,noreferrer");
                }}
                title={isBot ? "Open bot" : "Open site in new tab"}
                style={{
                  borderColor: theme === "light" ? "#ccc" : "#fff4",
                  color: theme === "light" ? "#000" : "#fff",
                }}
              >
                {isBot ? (
                  <>
                    <span className="bot-btn-label">Open bot</span>
                    <i className="fa fa-paper-plane bot-btn-icon" style={{ marginLeft: 6 }} />
                  </>
                ) : (
                  <i className="fa fa-external-link" />
                )}
              </button>
            )}
            {!project["guest-chat"] && (
              <button
                className={`project-modal-btn refresh-btn ${isSpinning ? "spinning" : ""}`}
                onClick={handleRefresh}
                title="Refresh"
                style={{
                  borderColor: theme === "light" ? "#ccc" : "#fff4",
                  color: theme === "light" ? "#000" : "#fff",
                }}
              >
                <i className="fa fa-refresh" />
              </button>
            )}
            <button
              className="project-modal-btn theme-btn"
              onClick={toggleTheme}
              title={
                theme === "light"
                  ? "Switch to Dark Theme"
                  : "Switch to Light Theme"
              }
              style={{
                borderColor: theme === "light" ? "#ccc" : "#fff4",
                color: theme === "light" ? "#000" : "#fff",
              }}
            >
              <i className={`fa fa-${theme === "light" ? "moon" : "sun"}`} />
            </button>
            <button
              className="project-modal-btn close-btn"
              onClick={onClose}
              title="Close"
              style={{
                borderColor: theme === "light" ? "#ccc" : "#fff4",
                color: theme === "light" ? "#000" : "#fff",
              }}
            >
              <i className="fa fa-times" />
            </button>
          </div>
        </div>
        <div
          className="project-modal-content"
          style={{
            background: project["guest-chat"] ? "#000" : (theme === "light" ? "#ffffff" : "#000c"),
            position: "relative",
          }}
        >
          {project["guest-chat"] ? (
            <GuestChat key={isOpen} />
          ) : (
          <>
          {isIframeLoading && (
            <div className={`project-modal-loading theme-${theme}`}>
              <i className="fa fa-globe project-modal-loading-icon" />
            </div>
          )}
          <iframe
            key={iframeKey}
            src={project["iframe-site"] || project.livesite}
            title={project.name}
            className={`project-modal-iframe`}
            allow="fullscreen"
            onLoad={() => setIsIframeLoading(false)}
            style={{
              ...(project["iframe-background"]
                ? { backgroundColor: project["iframe-background"] }
                : {}),
              position: "relative",
              zIndex: 2,
            }}
          />
          </>
          )}
        </div>
      </div>
    </div>
  );
}
